import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { RecentActivityFeed, type FeedItem } from '../../components/RecentActivityFeed';

interface Tenant {
  id: string;
  name: string;
  domain: string;
  memberCount: number;
  createdAt: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    callCount: number;
    costUsd: number;
  };
}

interface AdminStats {
  totalTenants: number;
  totalUsers: number;
  totalPersonas: number;
  platformUsage?: {
    inputTokens: number;
    outputTokens: number;
    callCount: number;
    costUsd: number;
  };
  tenantsUsage?: { orgId: string; orgName: string; costUsd: number; callCount: number }[];
}

export function AdminDashboardPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedOrgId, setFeedOrgId] = useState<string>('');
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedCursor, setFeedCursor] = useState<string | null>(null);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedLoading, setFeedLoading] = useState(true);

  const loadFeed = useCallback(async (orgId: string, cursor: string | null) => {
    setFeedLoading(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (orgId) params.set('orgId', orgId);
      if (cursor) params.set('cursor', cursor);
      const res = await api.get(`/admin/feed?${params.toString()}`);
      const data = res.data as { items: FeedItem[]; nextCursor: string | null };
      setFeedItems((prev) => (cursor ? [...prev, ...data.items] : data.items));
      setFeedCursor(data.nextCursor);
      setFeedHasMore(Boolean(data.nextCursor));
    } catch {
      if (!cursor) {
        setFeedItems([]);
        setFeedHasMore(false);
      }
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get('/admin/tenants'), api.get('/admin/stats')])
      .then(([tenantsRes, statsRes]) => {
        if (!cancelled) {
          setTenants(tenantsRes.data);
          setStats(statsRes.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTenants([]);
          setStats(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadFeed(feedOrgId, null);
  }, [feedOrgId, loadFeed]);

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
          <p className="text-gray-600 mt-1">
            Manage organizations, users, and personas across the platform.
          </p>
        </div>
        <div
          className="flex items-center justify-center py-16 text-gray-500"
          role="status"
          aria-live="polite"
        >
          <span className="inline-block animate-pulse">Loading platform data…</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
        <p className="text-gray-600 mt-1">
          Manage organizations, users, and personas across the platform.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link
          to="/admin/tenants"
          className="bg-white p-6 rounded-lg border shadow-sm block hover:border-red-300 hover:bg-red-50/30 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          <div className="text-3xl font-bold text-red-700">{stats?.totalTenants ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-1">Organizations</div>
        </Link>
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="text-3xl font-bold text-red-700">{stats?.totalUsers ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-1">Total Users</div>
        </div>
        <Link
          to="/admin/personas"
          className="bg-white p-6 rounded-lg border shadow-sm block hover:border-red-300 hover:bg-red-50/30 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          <div className="text-3xl font-bold text-red-700">{stats?.totalPersonas ?? '—'}</div>
          <div className="text-sm text-gray-500 mt-1">Personas in Catalog</div>
        </Link>
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <div className="text-3xl font-bold text-red-700">
            {stats?.platformUsage ? `$${stats.platformUsage.costUsd.toFixed(2)}` : '—'}
          </div>
          <div className="text-sm text-gray-500 mt-1">Platform est. AI cost (USD)</div>
        </div>
      </div>

      {stats?.tenantsUsage && stats.tenantsUsage.length > 0 && (
        <div className="bg-white rounded-lg border mb-8 overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900">Estimated cost by organization (USD)</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              From logged usage and list pricing — not invoices.
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y">
            {stats.tenantsUsage
              .slice()
              .sort((a, b) => b.costUsd - a.costUsd)
              .map((t) => (
                <Link
                  key={t.orgId}
                  to={`/admin/tenants/${t.orgId}`}
                  className="px-4 py-2 flex justify-between text-sm items-center hover:bg-gray-50 cursor-pointer"
                >
                  <span className="font-medium text-gray-900 hover:text-red-700">{t.orgName}</span>
                  <span className="font-medium text-gray-900">${t.costUsd.toFixed(2)}</span>
                </Link>
              ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-8">
        <Link
          to="/admin/personas"
          className="bg-red-700 text-white px-4 py-2 rounded-md font-medium hover:bg-red-800"
        >
          Manage Personas
        </Link>
        <Link
          to="/admin/video-avatars"
          className="bg-red-700 text-white px-4 py-2 rounded-md font-medium hover:bg-red-800"
        >
          Video Avatars
        </Link>
        <Link
          to="/admin/goals"
          className="bg-red-700 text-white px-4 py-2 rounded-md font-medium hover:bg-red-800"
        >
          Practice Goals
        </Link>
        <Link
          to="/admin/tenants"
          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-50"
        >
          View All Organizations
        </Link>
        <Link
          to="/admin/settings"
          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-50"
        >
          Platform Settings
        </Link>
      </div>

      <div className="bg-white rounded-lg border mb-8">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-gray-900">Recent Organizations</h2>
        </div>
        {tenants.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">No organizations yet.</div>
        ) : (
          <div className="divide-y">
            {tenants.slice(0, 10).map((t) => (
              <Link
                key={t.id}
                to={`/admin/tenants/${t.id}`}
                className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
              >
                <div>
                  <div className="font-medium text-gray-900">{t.name}</div>
                  <div className="text-sm text-gray-500">
                    {t.domain} &middot; {t.memberCount} members
                    {t.usage && t.usage.costUsd > 0 && (
                      <span> &middot; ~${t.usage.costUsd.toFixed(2)} AI</span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  {new Date(t.createdAt).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <section
        className="bg-white rounded-lg border overflow-hidden"
        aria-label="Recent activity (all organizations)"
      >
        <div className="p-4 border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Recent activity (all organizations)</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Practice sessions across every organization on the platform.
            </p>
          </div>
          <label className="text-sm flex items-center gap-2">
            <span className="text-gray-600">Organization</span>
            <select
              aria-label="Filter by organization"
              value={feedOrgId}
              onChange={(e) => setFeedOrgId(e.target.value)}
              className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white"
            >
              <option value="">All organizations</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <RecentActivityFeed
          items={feedItems}
          showOrg
          loading={feedLoading}
          hasMore={feedHasMore}
          onLoadMore={() => loadFeed(feedOrgId, feedCursor)}
          emptyLabel="No practice sessions yet."
        />
      </section>
    </div>
  );
}
